import React, { FC, ReactNode, useCallback, useLayoutEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { RouteComponentProps } from '@reach/router';
import {
  AdvancedButtonMode,
  getCookbookFilterConfig,
  PageHeader,
  SearchQuery,
  useAddToQuery,
  scrollToTop,
  NoTransformationMatch,
  usePlatformAnalytics,
  SearchOption,
  isCategoryOption,
  isComplexityOption,
  inMemoryFetchCookbookTransformations,
} from '../../common';
import { StandaloneCookbookData } from '../../data/StandaloneCookbookData';
import { messages } from '../../i18n';
import { navigateToPage } from '../../utils/navigateToPage';
import { StickyPageHeader } from '../../components/StickyPageHeader/StickyPageHeader';
import { TransformationCard } from '../../components/TransformationCard/TransformationCard';
import { useScrollToTop } from '../../utils/useScrollToTop';
import { getInitialSearchQuery } from '../../utils/urlFilters/urlFilters';
import { serializeUrlParams } from '../../utils/urlFilters/serializeUrlParams';
import {
  FilterPanelWrapper,
  Wrapper,
  PageTitle,
  HeaderContainer,
  Container,
  CardWrapper,
  PageStretcher,
  ListSearch,
  StyledCookbookFilterPanel,
} from './ListPage.styled';

export interface ListPageProps extends RouteComponentProps {
  initialData: StandaloneCookbookData;
}

export const ListPage: FC<React.PropsWithChildren<ListPageProps>> = ({ initialData, location }) => {
  useScrollToTop();
  const { categories, complexities } = initialData;
  const analytics = usePlatformAnalytics();
  const searchAnalytics = usePlatformAnalytics('cookbook search bar');
  const { formatMessage } = useIntl();
  const filtersConfig = getCookbookFilterConfig({ complexities, categories });
  const initialSearchQuery = getInitialSearchQuery(location?.search || '', filtersConfig);
  const [searchQuery, setQuery] = useState<SearchQuery>(initialSearchQuery);
  const onNoMatchClearButtonClick = useCallback(/* istanbul ignore next */ () => setQuery([]), [setQuery]);
  const openRecipeDetails = (slug: string) => {
    navigateToPage(`/cookbook/${slug}`);
    analytics('navigate', 'cookbook recipe modal');
  };
  const handleSearchClick = () => searchAnalytics('text search', 'intent to type text');
  const handleSearchClearButtonClick = () => {
    setQuery([]);
    searchAnalytics('text search', 'clear search');
  };
  const onNewTextChip = (text: string) => searchAnalytics('text search', text);
  const addToQuery = useAddToQuery({ searchQuery, setQuery }, scrollToTop);
  const handleQueryChipClick = useCallback(
    (option: SearchOption) => {
      addToQuery(option);
      // chip can be only category or complexity
      /* istanbul ignore next */
      const eventCategory = isCategoryOption(option) ? 'category' : 'complexity';
      /* istanbul ignore next */
      const eventLabel = (isCategoryOption(option) && option.category) || (isComplexityOption(option) && option.complexity) || 'unknown';
      analytics(eventCategory, eventLabel);
    },
    [addToQuery, analytics]
  );
  const transformations = inMemoryFetchCookbookTransformations(searchQuery);
  const [advancedButtonMode, setAdvancedButtonMode] = useState<AdvancedButtonMode>('collapsed');
  const isFiltersPanelVisible = advancedButtonMode === 'expanded';
  const handleAdvancedButtonClick = useCallback(() => {
    setAdvancedButtonMode(isFiltersPanelVisible ? 'collapsed' : 'expanded');
    searchAnalytics('show-hide', `${isFiltersPanelVisible ? 'hide' : 'show'} advanced search from icon`);
  }, [setAdvancedButtonMode, searchAnalytics, isFiltersPanelVisible]);

  useLayoutEffect(() => {
    const newUrlSearchQuery = serializeUrlParams(searchQuery);
    navigateToPage(`/cookbook${newUrlSearchQuery}`);
  }, [searchQuery]);

  return (
    <div>
      <StickyPageHeader>
        <HeaderContainer>
          <PageHeader.SearchWrapper>
            <PageTitle>
              <FormattedMessage {...messages['pages.list.header']} />
            </PageTitle>
            <ListSearch
              value={searchQuery}
              onChange={setQuery}
              onNewTextChip={onNewTextChip}
              onClear={handleSearchClearButtonClick}
              advancedButtonMode={advancedButtonMode}
              onAdvancedButtonClick={handleAdvancedButtonClick}
              rootProps={{
                onClick: handleSearchClick,
                placeholder: formatMessage(messages['pages.list.search-placeholder']),
              }}
            />
          </PageHeader.SearchWrapper>
        </HeaderContainer>
        {isFiltersPanelVisible && (
          <FilterPanelWrapper data-test="filter-panel-wrapper">
            <StyledCookbookFilterPanel query={searchQuery} setQuery={setQuery} filtersConfig={filtersConfig} />
          </FilterPanelWrapper>
        )}
      </StickyPageHeader>

      <Wrapper>
        {!!searchQuery.length && transformations.items.length === 0 && (
          <PageStretcher>
            <NoTransformationMatch
              title={formatMessage(messages['pages.list.search.no-results.title'])}
              subtitle={
                <FormattedMessage
                  {...messages['pages.list.search.no-results.subtitle']}
                  values={{
                    action: /* istanbul ignore next */ (...message: ReactNode[]) => (
                      <NoTransformationMatch.Link onClick={onNoMatchClearButtonClick}>{message}</NoTransformationMatch.Link>
                    ),
                  }}
                />
              }
            />
          </PageStretcher>
        )}

        {
          <Container>
            {transformations.items.map((transformation) => (
              <CardWrapper key={transformation.id}>
                <TransformationCard
                  {...transformation}
                  onReadMoreClick={() => openRecipeDetails(transformation.slug as string)}
                  onQueryChipClick={handleQueryChipClick}
                />
              </CardWrapper>
            ))}
          </Container>
        }
      </Wrapper>
    </div>
  );
};
